import dynamic from "next/dynamic";
import Head from "next/head";
import Theme from "../../components/Theme";
import AdaptiveCriticalImage from "../../components/atoms/AdaptiveCriticalImage";
import SubNavWithPagesHOC from "../../components/organisms/common/SubNavWithPagesHOC";
import PagesList from "../../components/organisms/integrations/PagesList";
import SubNav from "../../components/organisms/integrations/SubNav";
import NoSearchResultContainer from "../../components/organisms/integrations/NoSearchResultContainer";
import ResultsContainer from "../../components/organisms/integrations/ResultsContainer";
import AppStorage from "../../components/AppStorage";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";
const People = dynamic(() =>
  import("../../components/organisms/common/People")
);
const WideCTA = dynamic(() =>
  import("../../components/organisms/common/WideCTA")
);
const Masonry = dynamic(() =>
  import("../../components/organisms/industries/Masonry")
);
const TpWidget = dynamic(() =>
  import("../../components/atoms/document/TpWidget")
);
const TpWidgetSlider = dynamic(() =>
  import("../../components/organisms/common/TpWidgetSlider")
);
const CTA = dynamic(() => import("../../components/organisms/common/CTA"));
export async function getStaticProps(ctx) {
  const { locale } = ctx;
  let allData = {};

  // Fetch
  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/integrations/root/marketing.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/integrations/root/content.json`
  );
  const pagesResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/integrations/root/pages.json`
  );

  // Catch Error
  if (marketingResponse.status === 404 || contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();
  allData.pages = await pagesResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
    ...allData.pages,
  };

  return {
    props: {
      data,
      fallback: false,
    },
  };
}

const Hero = ({ data }) => {
  const appMode = process.env.APP_MODE;
  const signupLink = data?.cta?.href?.[appMode] || data?.cta?.href;
  return (
    <section className="container grid--default has-align-top hero--default lg:pt-10">
      <article className="text-left">
        <h1 className="text-xl lg:pt-3 lg:pr-5">{data.title}</h1>
        <p className="lg:maxw-md">{data.desc[0]}</p>
        <p className="lg:maxw-md lg:mt-1 lg:mb-5 md:mt-2 md:mb-2.5">
          {data.desc[1]}
        </p>
        <a
          href={signupLink}
          className="btn-primary md:w-100"
          id="integrations-hero-fold-cta"
        >
          <span>{data.cta.text}</span>
        </a>
      </article>{" "}
      <figure className="lg:maxw-lg md:mt-5">
        <picture>
          <AdaptiveCriticalImage
            src={data.images.fg.src}
            alt={data.images.fg.alt}
            mobile={data.images.fg.prefixes.mobile}
            dpi={data.images.fg.prefixes.dpi}
            className="img-fit"
            dimensions={[335, 320]}
          />
          <style jsx>{`
            picture :global(.fg-layer) {
              width: 136% !important;
              height: 117% !important;
            }
            @media (max-width: ${viewport.tablet.sm}) {
              picture :global(.fg-layer) {
                margin-left: 0;
                width: 100% !important;
                height: 100% !important;
              }
            }
          `}</style>
        </picture>
      </figure>
    </section>
  );
};

export const IntegrationFormCTA = ({ data }) => {
  const appMode = process.env.APP_MODE;
  const link = data?.cta?.href?.[appMode] || data?.cta?.href;
  return (
    <section
      className="bg-light-blue-grey lg:pt-5 lg:pb-20 md:mb-5"
      id="form-view"
    >
      <div className="container mx-auto lg:maxw-lg md:ml-3 md:mr-3">
        <article className="md:mb-5">
          <h2
            className="text-lg lg:text-center lg:mb-3 md:mb-2"
            dangerouslySetInnerHTML={{ __html: data.heading }}
          />
          <p
            className="lg:text-center"
            dangerouslySetInnerHTML={{ __html: data.desc }}
          />
        </article>
        <div className="btn-container mx-auto d-fx">
          <a
            href={link}
            className="btn-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            {data.cta.text}
          </a>
        </div>
      </div>
      <style jsx>{`
        .btn-container {
          display: flex;
          justify-content: center;
          margin-top: 40px;
        }
        .btn-container a {
          padding: 12px 40px;
          min-height: 48px;
          line-height: 24px;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .btn-container a {
            width: 100%;
          }
          article {
            text-align: left;
          }
        }
      `}</style>
    </section>
  );
};

const MasonrySection = ({ data }) => {
  return (
    <section className=" industry-masonry lg:mb-15 md:mb-10 md:pt-5">
      <h2 className="people-love-setmore container text-xl text-center lg:pt-10 lg:mb-7 md:mb-5">
        <span>{data.title}</span>
        <span className="md:d-block heart-glyph lg:ml-1 lg:mr-1">❤️</span>
        <span>Setmore</span>
      </h2>
      <TpWidgetSlider />
      <Masonry data={data.list} wrapClass="lg:mt-10" />
      <style jsx>{`
        .industry-masonry .text-xl {
          font-size: ${font.size.lg[4]};
        }
        .industry-masonry :global(.client-card.bg-yellow img) {
          margin-top: -80px;
          padding-top: 80px;
          padding-left: 32px;
          width: auto;
        }
        .industry-masonry :global(.client-card.bg-green img) {
          margin-left: 16px;
          width: auto;
        }
        .industry-masonry .text-xl {
          font-size: ${font.size.lg[4]};
        }
        .industry-masonry.bg-offset::after {
          top: 0;
          height: 500px;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .industry-masonry :global(.client-card.bg-green) {
            text-align: center;
          }
          .industry-masonry.bg-offset::after {
            top: -250px;
          }
        }
      `}</style>
    </section>
  );
};

export default function Index({ data, nonce }) {
  const { header, footer, seo, content, pages } = data;

  const allSocialmediaPages = [].concat(
    ...Object.values(pages.featuredList["social media"]?.lists ?? []),
    ...Object.values(pages.featuredList["social media"]?.zap ?? [])
  );
  const allPaymentPages = [].concat(
    ...Object.values(pages.featuredList["payment"]?.lists ?? []),
    ...Object.values(pages.featuredList["payment"]?.zap ?? [])
  );
  const allWebsitePages = [].concat(
    ...Object.values(pages.featuredList["website booking"]?.lists ?? []),
    ...Object.values(pages.featuredList["website booking"]?.zap ?? [])
  );
  const allCalendarSyncPages = [].concat(
    ...Object.values(pages.featuredList["calendar sync"]?.lists ?? []),
    ...Object.values(pages.featuredList["calender sync"]?.zap ?? [])
  );
  const allVideoMeetingPages = [].concat(
    ...Object.values(pages.featuredList["video meeting"]?.lists ?? []),
    ...Object.values(pages.featuredList["video meeting"]?.zap ?? [])
  );
  const allMarketingPages = [].concat(
    ...Object.values(pages.featuredList["marketing"]?.lists ?? []),
    ...Object.values(pages.featuredList["marketing"]?.zap ?? [])
  );
  const allSalesPages = [].concat(
    ...Object.values(pages.featuredList["sales and crm"]?.lists ?? []),
    ...Object.values(pages.featuredList["sales and crm"]?.zap ?? [])
  );
  const allBusinessPages = [].concat(
    ...Object.values(pages.featuredList["business"]?.lists ?? []),
    ...Object.values(pages.featuredList["business"]?.zap ?? [])
  );

  const allPages = [
    ...allSocialmediaPages,
    ...allPaymentPages,
    ...allWebsitePages,
    ...allCalendarSyncPages,
    ...allVideoMeetingPages,
    ...allMarketingPages,
    ...allSalesPages,
    ...allBusinessPages,
  ];
  pages["ctaOneContent"] = content.allIntegrationSectionOne;

  const subNavMasonryContainerData = {
    subNavData: content.subNav,
    noSearchResultData: content.searchResult,
    pagesListData: pages,
    allPagesList: allPages,
  };

  const SubNavMasonryContainer = SubNavWithPagesHOC(
    SubNav,
    PagesList,
    NoSearchResultContainer,
    ResultsContainer,
    subNavMasonryContainerData
  );

  return (
    <>
      <Head>
        <meta
          name="apple-itunes-app"
          content="app-id=665859578, app-argument=https://apps.apple.com/us/app/setmore-appointment-scheduling/id665859578?pid=ios_smart_banner&c=ios_campaign"
        />
      </Head>
      <Theme
        header={header}
        footer={footer}
        seo={seo}
        nonce={nonce}
        className="page--home integration-new integration_active integration-new-ab-testing industry-page-with-trust-pilot"
      >
        <AppStorage pageName="v2 integrations root" />
        <Hero data={content.hero} />
        <SubNavMasonryContainer />
        <IntegrationFormCTA data={content.integrationForm} />
        {content.hasOwnProperty("cta") && (
          <CTA
            data={content.cta}
            imgClassName={`img-fit`}
            className="premium-bg-cta bg-light-blue-grey md:pt-5 cta-section lg:pt-20 mg:pt-5 lg:pb-10"
            colorScheme="grey-white-blue"
          />
        )}
        <MasonrySection data={content.masonry} />
        <People />
        <WideCTA data={content.wideCTA} />
        <style global jsx>
          {`
            html {
              scroll-behavior: smooth;
            }
            .jsx-form.form-wrap {
              box-shadow: 0px 1px 3px rgba(48, 58, 71, 0.1),
                0px 1px 2px rgba(48, 58, 71, 0.2);
            }
            .integration-new .card--default.card--showcase > * {
              box-shadow: initial !important;
            }
            .integration-new .card--default.card--showcase > a:hover {
              box-shadow: 0 14px 32px 0 rgb(22 45 61 / 8%),
                0 1px 4px 0 rgb(22 45 61 / 10%) !important;
            }
            .bg-offset::after {
              background-color: ${colors.primitive.grey[600]};
            }
          `}
        </style>
        <TpWidget />
      </Theme>
    </>
  );
}
